import { createContext, type FC, forwardRef } from 'react';

import { FullPageContent } from '@/components/layout/full-page-content';
import { useCurrentAsset } from '@/hooks/use-current-asset';
import { IPCSRepository } from '@/pcs/domain/repositories/pcs.repository';

import { FilterBar } from '../components/filters/filter-bar';
import { NoPcsDataState } from '../components/no-pcs-data-state';
import { OverviewTable } from '../components/overview-table';
import { GetPowerAvailabilityPlot } from '../components/plots/power-availability/get-options';
import { GetPowerCapabilityPlot } from '../components/plots/power-capability/get-options';
import { GetPowerEfficiencyPlot } from '../components/plots/power-efficiency/get-options';
import { FilterBarSkeleton } from '../components/skeletons/FilterBarSkeleton';
import { OverviewTableSkeleton } from '../components/skeletons/OverviewTableSkeleton';
import { PlotSkeleton } from '../components/skeletons/PlotSkeleton';
import { useListPCSs } from '../queries/use-list-pcs';

const PageSkeleton: FC = () => {
  return (
    <FullPageContent
      className="min-h-[calc(100vh-64px)] w-full overflow-auto p-8"
      scrollable
    >
      <div className="container mx-auto flex flex-col space-y-8">
        {/* SECTION: PCS Filters */}
        <section aria-label="PCS Filters">
          <FilterBarSkeleton />
        </section>

        {/* SECTION: Overview Table */}
        <section
          id="overview-table"
          aria-label="Overview Table"
          className="overflow-x-auto"
        >
          <OverviewTableSkeleton />
        </section>

        {/* SECTION: Timeseries */}
        <section
          id="timeseries"
          aria-label="Timeseries"
          className="flex h-auto flex-col space-y-4"
        >
          <PlotSkeleton sectionName="PCS Efficiency" />
          <PlotSkeleton sectionName="PCS Capability" />
          <PlotSkeleton sectionName="PCS Availability" />
        </section>
      </div>
    </FullPageContent>
  );
};

export const PCSOverviewContext = createContext<
  { fullFrontendId: string } | undefined
>(undefined);

export const PCSOverviewPage = forwardRef<HTMLDivElement, any>(
  function (_props, ref) {
    const [filters, setFilters] = IPCSRepository.useGetFiltersFromQueryParams();

    const { asset, isLoading: isLoadingAsset } = useCurrentAsset();

    // Use PCS module's own query to check data availability
    const pcsListResult = useListPCSs({
      assetFullFrontendId: asset?.fullFrontendId ?? '',
    });

    const hasPcsData = (pcsListResult.data?.pcss.length ?? 0) > 0;
    const isLoadingPcs = pcsListResult.isLoading;

    if (isLoadingAsset || isLoadingPcs) {
      return <PageSkeleton />;
    }

    if (!asset) {
      return null;
    }

    // Show empty state when site doesn't have PCS data
    if (!hasPcsData) {
      return (
        <FullPageContent
          className="min-h-[calc(100vh-64px)] w-full overflow-auto p-8"
          scrollable
          testId="pcs-page"
        >
          <NoPcsDataState />
        </FullPageContent>
      );
    }

    return (
      <PCSOverviewContext.Provider
        value={{
          fullFrontendId: asset?.fullFrontendId ?? '',
        }}
      >
        <FullPageContent
          className="min-h-[calc(100vh-64px)] w-full overflow-auto p-8"
          scrollable
          testId="pcs-page"
        >
          <div className="container mx-auto flex min-w-[1000px] flex-col space-y-8">
            {/* SECTION: PCS Filters */}
            <section aria-label="PCS Filters" className="flex space-y-4">
              <FilterBar
                filters={filters}
                onChange={(f) => setFilters.push(f)}
              />
            </section>

            {/* SECTION: Overview Table */}
            <section
              id="overview-table"
              aria-label="Overview Table"
              className="overflow-x-auto"
            >
              <OverviewTable filters={filters} ref={ref} />
            </section>

            {/* SECTION: Timeseries */}
            <section
              id="timeseries"
              aria-label="Timeseries"
              className="flex h-auto flex-col space-y-4"
            >
              <GetPowerEfficiencyPlot filters={filters} />
              <GetPowerCapabilityPlot filters={filters} />
              <GetPowerAvailabilityPlot filters={filters} />
            </section>
          </div>
        </FullPageContent>
      </PCSOverviewContext.Provider>
    );
  },
);

PCSOverviewPage.displayName = 'PCSOverviewPage';
